import React from 'react';
import {Link} from 'react-router-dom';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import type { JsonNode, ProyectoDatosContablesDTO, ProyectoFormDTO } from '../../../../api/Objetos';
import { datosContablesApi } from '../../../../api/api';
import { FieldGrid, NumberField, TextField, JsonTableField, CurrencyField, ToggleField } from '../../../../ui/form/fields';
import { FormSection, hasPersistedId, useProjectReadOnly } from '../projectForm.helpers';
import { SectionAlerts, SectionSaveButton } from '../sectionCrudUtils';
import { useRegisterSectionSave } from '../SectionSaveCoordinator';
import { useMutation, useQuery } from '@tanstack/react-query';
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';
import {createDefaultAccountingTable} from '../accountingTable';

export function DatosContablesSection(props: { proyectoId: number; projectSlug: string; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({
        defaultValues: {
            datosContables: {
                datosVariables: createDefaultAccountingTable()
            },
        },
    });
    const { data: contablesData, refetch, isPending, error } = useQuery<ProyectoDatosContablesDTO | null>({
        queryKey: ['admin', 'proyectos', 'datos-contables', props.proyectoId],
        queryFn: () => datosContablesApi.get(props.proyectoId),
    });

    const [isLoaded, setIsLoaded] = React.useState(false);
    const saveMutation = useMutation({
        mutationFn: (data: ProyectoDatosContablesDTO) => datosContablesApi.save(props.proyectoId, data),
    });

    React.useEffect(() => {
        if (contablesData && !isLoaded) {
            let variablesParsed: JsonNode = contablesData.datosVariables;
            if (typeof variablesParsed === 'string') {
                try { variablesParsed = JSON.parse(variablesParsed); } catch (e) {}
            }

            if (!variablesParsed || typeof variablesParsed !== 'object' || !Array.isArray((variablesParsed as any).rows)) {
                variablesParsed = createDefaultAccountingTable();
            }

            form.reset({
                datosContables: {
                    ...contablesData,
                    datosVariables: variablesParsed
                }
            });

            setIsLoaded(true);
        }
    }, [contablesData, isLoaded, props.proyectoId, form]);

    const handleSave = form.handleSubmit(async (value) => {
        if (value.datosContables) {
            const saved = await saveMutation.mutateAsync(value.datosContables);
            form.reset({datosContables: saved});
            await refetch();
        }
    });

    useRegisterSectionSave({
        id: 'datos-contables',
        isDirty: form.formState.isDirty,
        isSaving: saveMutation.isPending,
        save: async () => { await handleSave(); },
    });

    return (
        <FormProvider {...form}>
            <DatosContablesSectionFields 
                proyectoId={props.proyectoId} 
                projectSlug={props.projectSlug}
                showIds={Boolean(props.showIds)}
                isLoading={isPending && !isLoaded}
                loadError={error as Error}
            />
            {!readOnly && (
                <>
                    <SectionSaveButton onClick={() => void handleSave()} busy={saveMutation.isPending}/>
                </>
            )}
        </FormProvider>
    );
}

function DatosContablesSectionFields(props: { proyectoId: number; projectSlug: string; showIds: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const showId = (path: string) => props.showIds && hasPersistedId(form.watch(path as never));
    const draftKey = `proyecto-${props.proyectoId}`;
    const readOnly = useProjectReadOnly();
    return (
        <FormSection
            id="datos-contables"
            title={t('datosContablesSection.title')}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <ToggleField
                        label={t('datosContablesSection.public')}
                        name="datosContables.publico"
                        register={form.register}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            <div className="stack">
                <FieldGrid>
                    {showId('datosContables.id') ? <NumberField label={t('datosContablesSection.id')} name="datosContables.id" register={form.register}/> : null}
                    <TextField label={t('datosContablesSection.gemaProjectNumber')} name="datosContables.numeroProyectoContableGema" register={form.register}/>
                    <TextField label={t('datosContablesSection.ministryGrantPayment')} name="datosContables.pagoSubvencionMinisterio" register={form.register}/>
                    <CurrencyField label={t('datosContablesSection.totalAmount')} name="datosContables.importeTotal" register={form.register} />
                    <CurrencyField label={t('datosContablesSection.europeanFinancing')} name="datosContables.importeFinanciacionEuropea" register={form.register} />
                    <CurrencyField label={t('datosContablesSection.municipalContribution')} name="datosContables.importeAportacionMunicipal" register={form.register} />
                </FieldGrid>
                <div className="accountingTableHeader">
                    <Link
                        className="btn btnSecondary btnSmall"
                        to={`/administracion/proyectos/${props.projectSlug}/editor-json/datos-contables-variables`}
                    >
                        {t('datosContablesSection.openFullEditor')}
                    </Link>
                </div>
                <JsonTableField
                    label={t('datosContablesSection.variableAccountingData')}
                    name="datosContables.datosVariables"
                    control={form.control}
                    projectId={props.proyectoId}
                    projectSlug={props.projectSlug}
                    draftKey={draftKey}
                    slug="datos-contables-variables"
                    previewMode="table"
                />
            </div>
        </FormSection>
    );
}
